/**
 * PausedJobsTab — the "Paused" (multi-day) tab body of the Jobs page,
 * extracted from JobsEnhanced.jsx (Jun 2026) to shrink that file.
 * All state and handlers stay in the parent and arrive as props.
 */
import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import Pagination from '../Pagination';
import LabelChip from '../LabelChip';
import TeamWorkingNow from './TeamWorkingNow';
import { formatTimeWithTimezone, formatDateTimeWithTimezone } from '../../utils/timezone';
import { User, MapPin, Clock, Camera, UserCog, Pause, CheckCircle2, StickyNote, Wrench, Play, Activity, Edit2, Archive, Trash2 } from 'lucide-react';
import JobAssignees from './JobAssignees';
import { TeamViaBadge } from './TeamViaBadge';

export const PausedJobsTab = ({
  pausedJobs,
  pausedJobsPage,
  user,
  canManageJobs,
  canWorkJob,
  getUserName,
  selectedJobIds,
  toggleSelectJob,
  unreadNotes,
  labelsById,
  setDetailJob,
  setWorkbenchJob,
  users = [],
  onCrewChanged,
  setQuickNoteJob,
  setSelectedJob,
  setShowReassignDialog,
  openPhotoGallery,
  handlePhotoUpload,
  openAdminPhotos,
  uploadingPhoto,
  handleResumeJob,
  openEditDialog,
  handleArchiveJob,
  handleDeleteJob,
  canDeleteJobs = false,
  handleCompleteJob,
  isLoading,
  pages,
  setPages,
  pageSize,
  setPageSize,
}) => (
  <>
    {pausedJobs.length === 0 ? (
      <Card className="border-2 border-dashed border-[#FFA000]">
        <CardContent className="p-12 text-center">
          <Pause className="w-16 h-16 mx-auto text-yellow-400 mb-4" />
          <p className="text-slate-500 text-lg font-medium">No paused jobs</p>
          <p className="text-gray-400 text-sm mt-2">Jobs paused for multi-day work will appear here</p>
        </CardContent>
      </Card>
    ) : (
      <div className="space-y-4">
        {pausedJobsPage.map((job) => {

          // Calculate cumulative hours from work sessions
          let totalHours = 0;
          let totalMinutes = 0;

          if (job.work_sessions && job.work_sessions.length > 0) {
            // Sessions use started_at / ended_at (legacy: start_time / end_time).
            let totalMs = 0;
            job.work_sessions.forEach(session => {
              const s = session.started_at || session.start_time;
              const e = session.ended_at || session.end_time;
              if (s && e) totalMs += Math.max(0, new Date(e) - new Date(s));
            });
            const diffMins = Math.floor(totalMs / 60000);
            totalHours = Math.floor(diffMins / 60);
            totalMinutes = diffMins % 60;
          }

          const cumulativeDuration = `${totalHours}h ${totalMinutes}m`;

          return (
            <Card key={job.id} className="relative shadow-md border-l-4 border-[#FFA000] bg-[#1A2433]">
              {canManageJobs && (
                <input
                  type="checkbox"
                  aria-label={`Select job ${job.job_number || job.title}`}
                  checked={selectedJobIds.has(job.id)}
                  onChange={() => toggleSelectJob(job.id)}
                  className="absolute top-3 right-3 w-4 h-4 cursor-pointer accent-blue-500 z-10"
                  data-testid={`select-job-${job.id}`}
                  title="Select for bulk action"
                />
              )}
              <CardContent className="p-5">
                <div className="space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-lg font-bold text-white flex items-baseline gap-2 flex-wrap">{job.job_number && <span className="text-xs font-mono text-[#1E88E5] bg-[#1E88E5]/10 px-2 py-0.5 rounded" data-testid={`job-number-${job.id}`}>{job.job_number}</span>}<button type="button" onClick={(e) => { e.stopPropagation(); setDetailJob(job); }} className="text-left hover:text-blue-400 hover:underline transition-colors cursor-pointer" data-testid={`open-job-detail-${job.id}`} title="Open job details (notes, files, activity)">{job.title}</button>{unreadNotes[job.id] > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white animate-pulse" title={`${unreadNotes[job.id]} unread note${unreadNotes[job.id] === 1 ? '' : 's'}`} data-testid={`unread-notes-badge-${job.id}`}>{unreadNotes[job.id]} new</span>}{canManageJobs && <button type="button" onClick={(e) => { e.stopPropagation(); setWorkbenchJob(job); }} className="text-slate-500 hover:text-[#1E88E5] transition-colors" data-testid={`open-workbench-${job.id}`} title="Job Workbench — crew, time and costs"><Wrench className="w-4 h-4" /></button>}</h3><TeamViaBadge job={job} />
                        <Badge className="bg-[#0F1923]0 text-white">⏸️ Paused</Badge>
                        {job.is_extended && (
                          <Badge
                            className="border text-white"
                            style={{ background: 'rgba(139,92,246,0.15)', borderColor: '#8B5CF6' }}
                            data-testid={`extended-badge-${job.id}`}
                          >
                            Extended
                          </Badge>
                        )}
                        {job.photos_required && (
                          <Badge className="bg-[#1A2433] text-yellow-800 border-[#FFA000] border">
                            <Camera className="w-3 h-3 mr-1" />
                            Photos Required
                          </Badge>
                        )}
                      </div>
                      {(job.label_ids || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`job-labels-row-${job.id}`}>
                          {(job.label_ids || []).map((lid) => labelsById[lid]).filter(Boolean).map((l) => (
                            <LabelChip key={l.id} label={l} size="sm" />
                          ))}
                        </div>
                      )}
                      {job.description && (
                        <p className="text-sm text-slate-400 mb-2">{job.description}</p>
                      )}
                      {(job.paused_reason || job.paused_notes) && (
                        <div
                          className="mt-2 p-3 rounded-lg text-sm"
                          style={{ background: 'rgba(255,160,0,0.08)', border: '1px solid rgba(255,160,0,0.25)' }}
                          data-testid={`paused-reason-${job.id}`}
                        >
                          <div className="text-[#FFA000] font-medium">
                            Paused: {job.paused_reason || 'No reason provided'}
                          </div>
                          {job.paused_notes && (
                            <div className="text-slate-300 text-xs mt-1 whitespace-pre-wrap">
                              {job.paused_notes}
                            </div>
                          )}
                          {job.paused_by_name && (
                            <div className="text-slate-500 text-xs mt-1">
                              by {job.paused_by_name}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Job Info Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm bg-[#1A2433] p-3 rounded-lg">
                    <JobAssignees job={job} getUserName={getUserName} canManage={canManageJobs} users={users} onChanged={onCrewChanged} iconClass="text-yellow-600" />
                    {job.job_address && (
                      <div className="flex items-center space-x-2 text-slate-300">
                        <MapPin className="w-4 h-4 text-yellow-600" />
                        <span className="font-medium">Location:</span>
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.job_address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#1E88E5] hover:underline truncate"
                        >
                          View on Map
                        </a>
                      </div>
                    )}
                    <div className="flex items-center space-x-2 text-slate-300">
                      <Clock className="w-4 h-4 text-yellow-600" />
                      <span className="font-medium">First Started:</span>
                      <span>{formatTimeWithTimezone(job.started_at, user?.timezone || 'America/Toronto')}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-yellow-700 font-semibold">
                      <Clock className="w-4 h-4" />
                      <span className="font-medium">Total Duration:</span>
                      <span>{cumulativeDuration}</span>
                    </div>
                    {job.customer_name && (
                      <div className="flex items-center space-x-2 text-slate-300">
                        <User className="w-4 h-4 text-yellow-600" />
                        <span className="font-medium">Customer:</span>
                        <span>{job.customer_name}</span>
                      </div>
                    )}
                    {job.photos && job.photos.length > 0 && (
                      <div className="flex items-center space-x-2 text-slate-300">
                        <Camera className="w-4 h-4 text-yellow-600" />
                        <span className="font-medium">Photos:</span>
                        <button
                          onClick={() => openPhotoGallery(job)}
                          className="text-[#1E88E5] hover:underline font-medium"
                        >
                          {job.photos.length} uploaded
                        </button>
                      </div>
                    )}
                    {(job.started_on_behalf || job.completed_on_behalf || job.paused_on_behalf) && (
                      <div className="flex items-center space-x-2 text-amber-400 text-xs" data-testid={`on-behalf-audit-${job.id}`}>
                        <UserCog className="w-3.5 h-3.5" />
                        <span className="font-medium">
                          {job.completed_on_behalf
                            ? `Completed on behalf by ${job.completed_by_name || 'admin'}`
                            : job.paused_on_behalf
                              ? `Paused on behalf by ${job.paused_by_name || 'admin'}`
                              : `Started on behalf by ${job.started_by_name || 'admin'}`}
                        </span>
                      </div>
                    )}
                  </div>

                  <TeamWorkingNow job={job} getUserName={getUserName} currentUserId={user?.id} timezone={user?.timezone} onChanged={onCrewChanged} />

                  {/* Work Sessions Breakdown */}
                  {job.work_sessions && job.work_sessions.length > 0 && (
                    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-lg border border-[#2D3748]" data-testid={`sessions-timeline-${job.id}`}>
                      <h4 className="font-semibold text-white mb-3 flex items-center">
                        <Activity className="w-4 h-4 mr-2 text-yellow-600" />
                        Work Sessions ({job.work_sessions.length})
                      </h4>
                      <div className="space-y-2">
                        {job.work_sessions.map((session, index) => {
                          // Session duration (use started_at/ended_at; fall back to hours_worked)
                          let sessionDuration = 'In Progress';
                          const startISO = session.started_at || session.start_time;
                          const endISO = session.ended_at || session.end_time;
                          if (endISO && startISO) {
                            const start = new Date(startISO);
                            const end = new Date(endISO);
                            const diffMs = end - start;
                            const diffMins = Math.floor(diffMs / 60000);
                            const hours = Math.floor(diffMins / 60);
                            const minutes = diffMins % 60;
                            sessionDuration = `${hours}h ${minutes}m`;
                          } else if (typeof session.hours_worked === 'number' && session.hours_worked > 0) {
                            const hours = Math.floor(session.hours_worked);
                            const minutes = Math.round((session.hours_worked - hours) * 60);
                            sessionDuration = `${hours}h ${minutes}m`;
                          }

                          const sessionWorker = session.worker_name || getUserName(session.worker_id || session.user_id);

                          return (
                            <div key={session.id || `${session.started_at || session.start || index}`} className="flex items-center justify-between bg-[#1A2433] p-3 rounded border border-[#2D3748]">
                              <div className="flex items-center space-x-3">
                                <Badge variant="outline" className="bg-[#1A2433] text-yellow-800">
                                  Session {index + 1}
                                </Badge>
                                <div className="text-sm">
                                  <div className="font-medium text-white">
                                    {formatDateTimeWithTimezone(session.started_at || session.start_time, user?.timezone || 'America/Toronto')}
                                  </div>
                                  <div className="text-slate-400">
                                    Worker: {sessionWorker}
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-bold text-yellow-700">{sessionDuration}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  {canWorkJob(job) && (
                    <div className="flex flex-wrap gap-2 pt-3 border-t">
                      <Button
                        size="sm"
                        onClick={() => handleResumeJob(job.id)}
                        disabled={isLoading}
                        className="bg-yellow-600 hover:bg-yellow-700 text-white"
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Resume Job
                      </Button>
                      <input
                        type="file"
                        id={`photo-upload-paused-${job.id}`}
                        accept="image/*"
                        multiple
                        onChange={(e) => handlePhotoUpload(e, job)}
                        className="hidden"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => document.getElementById(`photo-upload-paused-${job.id}`).click()}
                        disabled={uploadingPhoto || (job.photos && job.photos.length >= 10)}
                        className="border-[#FFA000] text-yellow-700 hover:bg-[#0F1923]"
                      >
                        <Camera className="w-4 h-4 mr-1" />
                        Add Photos ({job.photos ? job.photos.length : 0}/10)
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setQuickNoteJob(job)}
                        className="border-amber-500 text-amber-300 hover:bg-amber-500/10"
                        data-testid={`add-note-job-${job.id}`}
                        title="Add a note to this job"
                      >
                        <StickyNote className="w-4 h-4 mr-1" />
                        Add a Note
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleCompleteJob(job.id)}
                        disabled={isLoading}
                        className="bg-[#00C896] hover:bg-green-700 text-white"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Complete Job
                      </Button>
                    </div>
                  )}

                  {/* Admin/Supervisor Actions for Paused Jobs */}
                  {canManageJobs && !canWorkJob(job) && (
                    <div className="flex flex-wrap gap-2 pt-3 border-t">
                      <Button
                        size="sm"
                        onClick={() => handleResumeJob(job.id)}
                        disabled={isLoading}
                        className="bg-yellow-600 hover:bg-yellow-700 text-white"
                        data-testid={`admin-resume-job-${job.id}`}
                        title={`Resume on behalf of ${getUserName(job.assigned_to)}`}
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Resume (on behalf)
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleCompleteJob(job.id)}
                        disabled={isLoading}
                        className="bg-[#00C896] hover:bg-green-700 text-white"
                        data-testid={`admin-complete-paused-job-${job.id}`}
                        title={`Complete on behalf of ${getUserName(job.assigned_to)}`}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Complete (on behalf)
                      </Button>
                      <input
                        type="file"
                        id={`photo-upload-paused-admin-${job.id}`}
                        accept="image/*"
                        multiple
                        onChange={(e) => handlePhotoUpload(e, job)}
                        className="hidden"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => (openAdminPhotos ? openAdminPhotos(job) : document.getElementById(`photo-upload-paused-admin-${job.id}`).click())}
                        data-testid={`admin-add-photos-${job.id}`}
                        disabled={uploadingPhoto || (job.photos && job.photos.length >= 10)}
                        className="border-[#FFA000]/60 text-amber-300 hover:bg-[#0F1923] disabled:opacity-40"
                      >
                        <Camera className="w-4 h-4 mr-1" />
                        Add Photos ({job.photos ? job.photos.length : 0}/10)
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setQuickNoteJob(job)}
                        className="border-amber-500 text-amber-300 hover:bg-amber-500/10"
                        data-testid={`add-note-job-${job.id}`}
                        title="Add a note to this job"
                      >
                        <StickyNote className="w-4 h-4 mr-1" />
                        Add a Note
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedJob(job);
                          setShowReassignDialog(true);
                        }}
                        className={job.assigned_to ? "border-[#2D3748] text-blue-700 hover:bg-[#0F1923]" : "border-emerald-500 text-emerald-700 hover:bg-emerald-50"}
                        data-testid={`reassign-job-${job.id}`}
                      >
                        <UserCog className="w-4 h-4 mr-1" />
                        {job.assigned_to ? 'Reassign' : 'Assign worker'}
                      </Button>
                    </div>
                  )}

                  {/* Manage row — admins/supervisors can edit, archive or delete a job in ANY status (Oct 2026) */}
                  {canManageJobs && openEditDialog && (
                    <div className="flex flex-wrap gap-2 pt-3 border-t border-dashed border-[#2D3748]" data-testid={`manage-row-paused-${job.id}`}>
                      <Button size="sm" variant="outline" onClick={() => openEditDialog(job)} className="border-[#2D3748] text-slate-300 hover:bg-[#1A2433]" data-testid={`edit-job-paused-${job.id}`}>
                        <Edit2 className="w-4 h-4 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleArchiveJob(job.id)} disabled={isLoading} className="border-[#FFA000] text-[#FFA000] hover:bg-[#FFA000]/10" data-testid={`archive-job-paused-${job.id}`} title="Archive for later (seasonal hold, cancelled, on hold) — restore any time">
                        <Archive className="w-4 h-4 mr-1" /> Archive
                      </Button>
                      {canDeleteJobs && (
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteJob(job.id)} disabled={isLoading} className="ml-auto" data-testid={`delete-job-paused-${job.id}`}>
                          <Trash2 className="w-4 h-4 mr-1" /> Delete
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    )}
    <Pagination
      total={pausedJobs.length}
      page={pages.paused}
      pageSize={pageSize}
      onPageChange={(p) => setPages(s => ({ ...s, paused: p }))}
      onPageSizeChange={setPageSize}
      testId="paused-pagination"
      itemLabel="job"
    />
  </>
);

export default PausedJobsTab;
